import { useState } from "react";
import { useWizard } from "components/elements/Wizard/useWizard";
import { useSpeckleVersions } from "../../lib/hooks/useSpeckleVersions";
import { classes, nameof } from "lib/actions/util";
import { getModelTextSearch } from "lib/actions/getModelTextSearch";
import { ErrorMessage, Field, useFormikContext } from "formik";
import { SpeckleImportModel } from "../types/SpeckleImportModel";
import { SpeckleVersionModel } from "../types/SpeckleVersionModel";
import Loading from "components/elements/Loading/Loading";
import FormError from "components/elements/Forms/FormError";
import SpeckleTitle from "../SpeckleTitle";
import InputSearch from "components/elements/Input/InputSearch";
import SpeckleRefreshButton from "../SpeckleRefreshButton";
import SpeckleBreadcrumbs, {
  SpeckleCrumb,
  SpeckleCrumbSeparator,
} from "../SpeckleBreadcrumbs";
import SpeckleRadioInputVersions from "../controls/SpeckleRadioInputVersions";
import Button from "components/elements/Button/Button";
import {
  getClearedValuesForStep,
  SpeckleImportStep,
} from "./getClearedValuesForStep";

export default function SpeckleImportVersionStep() {
  const [searchTerm, setSearchTerm] = useState("");
  const { values, isValid, isSubmitting, setValues, setTouched, setErrors } =
    useFormikContext<SpeckleImportModel>();
  const { stepJump } = useWizard();
  const {
    versions: versionsData,
    isLoading,
    hasError,
    refresh,
  } = useSpeckleVersions(
    parseInt(values.connectedAccountId),
    values.projectId,
    values.modelId,
  );

  function handleStepJump(step: SpeckleImportStep) {
    const clearedValues = getClearedValuesForStep(step, values);
    setValues(clearedValues, false);
    setTouched({}, false);
    setErrors({});
    stepJump(step);
  }

  const isShowingSubmit = !!values.versionId && isValid;
  const account = values.connectedAccountId || "Account";
  const project = values.projectName || "Project";
  const model = values.modelName || "Model";
  const { search } = getModelTextSearch<SpeckleVersionModel>(
    searchTerm,
    (version) => version.message,
  );
  const versions = search(versionsData) || [];
  const versionItems =
    !isLoading &&
    !hasError &&
    versions.map((x) => ({
      value: x.model.id,
      label: x.model.message,
      date: x.model.createdOn,
      hash: x.model.id,
    }));

  function onRefresh() {
    refresh();
  }

  async function onSelect(value: string) {
    setValues(
      (values) => ({
        ...values,
        versionId: value,
      }),
      false,
    );
  }

  return (
    <div
      className={classes("grid grid-cols-2 gap-8", isShowingSubmit && "pb-20")}
    >
      <div className="flex flex-col justify-between">
        <header>
          <SpeckleTitle />
          <h1 className="mt-10 flex w-full flex-col text-2xl font-thin">
            <div>
              Select a <span className="font-normal">Version</span> of{" "}
            </div>
            <div className="line-clamp-3 w-5/6 overflow-hidden text-ellipsis break-words font-normal">
              {model}
            </div>{" "}
            to bring into&nbsp;Keyframe
          </h1>
        </header>
      </div>
      <div className="space-y-5">
        <SpeckleBreadcrumbs>
          <SpeckleCrumb
            onClick={() => handleStepJump(SpeckleImportStep.Account)}
          >
            {account}
          </SpeckleCrumb>
          <SpeckleCrumbSeparator />
          <SpeckleCrumb
            onClick={() => handleStepJump(SpeckleImportStep.Workspace)}
          >
            {values.workspaceName}
          </SpeckleCrumb>
          <SpeckleCrumbSeparator />
          <SpeckleCrumb
            onClick={() => handleStepJump(SpeckleImportStep.Project)}
          >
            {project}
          </SpeckleCrumb>
          <SpeckleCrumbSeparator />
          <SpeckleCrumb onClick={() => handleStepJump(SpeckleImportStep.Model)}>
            {model}
          </SpeckleCrumb>
          <SpeckleCrumbSeparator />
          <SpeckleCrumb active>Versions</SpeckleCrumb>
        </SpeckleBreadcrumbs>
        <div className="flex items-center justify-between pr-5 text-xs">
          <span>{versionsData?.length ?? 0} versions available</span>
          <SpeckleRefreshButton onClick={onRefresh} />
        </div>
        <div className="h-[36rem] space-y-6 overflow-y-auto pb-8 pr-4">
          <InputSearch
            defaultValue={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search versions"
          />
          <Field
            name={nameof(values, "versionId")}
            as={SpeckleRadioInputVersions}
            items={versionItems}
            model={model}
            onSelect={onSelect}
            disabled={isSubmitting}
          />
          {isLoading && <Loading />}
          {hasError && <span className="text-center">Error</span>}
        </div>
        <ErrorMessage
          name={nameof(values, "versionId")}
          component={FormError}
        />
      </div>

      {isShowingSubmit && (
        <footer
          className="absolute inset-x-0 bottom-0 flex h-20 justify-end bg-white 
          p-4 shadow-[0_-3px_6px] shadow-gray-300"
        >
          <Button type="submit" disabled={isSubmitting}>
            {!isSubmitting ? "Import to Keyframe" : "Working..."}
          </Button>
        </footer>
      )}
    </div>
  );
}
